import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useData } from '../../contexts/DataContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../App';
import { hasProAccess } from '../../utils/trialHelpers';
import Modal from '../common/Modal';
import Button from '../common/Button';
import ProBadge from '../common/ProBadge';
import { toast } from 'sonner';
import DriveAttachGate from '../files/DriveAttachGate';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { uploadTaskFilesToDrive, formatUploadErrors } from '../../utils/driveUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditTaskModal = ({ isOpen, onClose, task, onSuccess }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const { contacts, categories, updateTask, fetchContacts, fetchCategories, loading: dataLoading } = useData();
  // Visibility editing only matters for tasks living inside an organization
  // workspace. Determined from the task itself so users can flip the field
  // even when their *active* workspace is different.
  const isOrgTask = !!task?.org_id;
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  
  // Conflict detection state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState({ exactMatches: [], conflicts: [] });
  const [pendingTaskData, setPendingTaskData] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    due_date: '',
    time: '',
    category: '',
    priority: 'Medium',
    location: '',
    contact_id: '',
    notes: '',
    recurring: false,
    recurrence_pattern: '',
    recurrence_end_date: '',
    reminder_enabled: false,
    visibility: 'team',
  });

  // All hooks must be called before any conditional returns
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  }, []);

  const removeFile = useCallback((index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const removeExistingFile = useCallback(async (fileId) => {
    if (!window.confirm('Delete this file? Files you uploaded are also removed from your Google Drive.')) return;
    try {
      await axios.delete(`${API}/tasks/${task.id}/attachments/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setExistingFiles(prev => prev.filter(file => file.id !== fileId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  }, [task]);

  // Initialize form data when task changes
  useEffect(() => {
    if (task && isOpen) {
      setFormData({
        title: task.title || '',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        time: task.time || '',
        category: task.category || '',
        priority: task.priority || 'Medium',
        location: task.location || '',
        contact_id: task.contact_id || '',
        notes: task.notes || '',
        recurring: task.recurring || false,
        recurrence_pattern: task.recurrence_pattern || '',
        recurrence_end_date: task.recurrence_end_date ? task.recurrence_end_date.split('T')[0] : '',
        reminder_enabled: task.reminder_enabled || false,
        visibility: task.visibility || 'team',
      });
      setExistingFiles(task.attachments || []);
      setAttachments([]);
    }
  }, [task, isOpen]);

  // Load contacts and categories when modal opens
  useEffect(() => {
    if (isOpen) {
      // Ensure contacts and categories are loaded
      if (contacts.length === 0) {
        fetchContacts();
      }
      if (categories.length === 0) {
        fetchCategories();
      }
    }
  }, [isOpen, contacts.length, categories.length, fetchContacts, fetchCategories]);

  // Audio recording functions
  const startRecording = async () => {
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert("Audio recording is not supported in this browser or requires HTTPS.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        setAttachments(prev => [...prev, file]);
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      if (error.name === 'NotAllowedError') {
        alert("Microphone access denied. Please allow microphone access in your browser settings and try again.");
      } else if (error.name === 'NotFoundError') {
        alert("No microphone found. Please connect a microphone and try again.");
      } else if (error.name === 'NotSupportedError') {
        alert("Audio recording is not supported in this browser.");
      } else {
        alert("Unable to access microphone. Please check your browser settings and permissions.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // Early return AFTER all hooks
  if (!isOpen) return null;

  const checkConflicts = async (taskData, taskId) => {
    try {
      const response = await axios.post(`${API}/tasks/check-conflicts`, {
        due_date: taskData.due_date,
        time: taskData.time,
        task_id: taskId  // Exclude current task when editing
      }, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { has_exact_match: false, has_conflicts: false, exact_match_tasks: [], conflict_tasks: [] };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task || !task.id) return;

    setLoading(true);
    try {
      // Prepare task data
      const taskData = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null
      };
      // Personal tasks have no visibility concept — keep the payload clean.
      if (!isOrgTask) {
        delete taskData.visibility;
      }

      // Check for conflicts (excluding current task)
      const conflictCheck = await checkConflicts(taskData, task.id);
      
      if (conflictCheck.has_exact_match || conflictCheck.has_conflicts) {
        // Show conflict modal
        setConflictData({
          exactMatches: conflictCheck.exact_match_tasks || [],
          conflicts: conflictCheck.conflict_tasks || []
        });
        setPendingTaskData(taskData);
        setShowConflictModal(true);
        setLoading(false);
        return; // Stop here, let user decide
      }

      // No conflicts, proceed with update
      await updateTaskDirectly(taskData);
    } catch (error) {
      console.error("Error updating task:", error);
      alert("Failed to update task. Please try again.");
      setLoading(false);
    }
  };

  const updateTaskDirectly = async (taskData) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, taskData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Upload new attachments if any
      if (attachments.length > 0) {
        setUploadProgress({ current: 0, total: attachments.length, filename: '' });
        
        const uploadErrors = await uploadTaskFilesToDrive(task.id, attachments, {
          onFileStart: (i, file) => setUploadProgress({ current: i + 1, total: attachments.length, filename: file.name })
        });
        if (uploadErrors.length) toast.error(`Some files were not attached:\n${formatUploadErrors(uploadErrors)}`);
      }

      setShowConflictModal(false);
      setPendingTaskData(null);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating task:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAnyway = async () => {
    if (pendingTaskData) {
      setLoading(true);
      await updateTaskDirectly(pendingTaskData);
    }
  };

  const handleEditTime = () => {
    setShowConflictModal(false);
    setPendingTaskData(null);
    // Form stays open for user to edit
  };

  const getFileIcon = (file) => {
    const contentType = file.content_type || file.type;
    if (contentType?.startsWith('image/')) {
      return '🖼️';
    } else if (contentType?.startsWith('audio/')) {
      return '🎵';
    } else if (contentType?.includes('pdf')) {
      return '📄';
    } else if (contentType?.includes('document') || contentType?.includes('word')) {
      return '📝';
    }
    return '📎';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('editTask')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('title')} *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="mobile-input"
              required
              placeholder={t('enterTaskTitle')}
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('dueDate')} *</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleInputChange}
              className="mobile-input"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('time')} *</label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleInputChange}
              className="mobile-input"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('category')} *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="mobile-input"
              required
              disabled={dataLoading?.categories}
            >
              <option value="">
                {dataLoading?.categories ? t('loadingCategories') : t('selectCategory')}
              </option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('priority')} *</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className="mobile-input"
              required
            >
              <option value="Low">{t('low')}</option>
              <option value="Medium">{t('medium')}</option>
              <option value="High">{t('high')}</option>
            </select>
          </div>

          {/* Visibility — only for tasks that belong to an organization
              workspace. Lets owners flip a team task to private (or back). */}
          {isOrgTask && (
            <div className="md:col-span-2" data-testid="edit-task-visibility-row">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                {t('visibility') || 'Visibility'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, visibility: 'team' }))}
                  data-testid="edit-visibility-team-button"
                  className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                    formData.visibility === 'team'
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true">🏢</span>
                    <span>{t('visibilityTeam') || 'Team'}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('visibilityTeamHint') || 'Visible to everyone'}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, visibility: 'private' }))}
                  data-testid="edit-visibility-private-button"
                  className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                    formData.visibility === 'private'
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true">🔒</span>
                    <span>{t('visibilityPrivate') || 'Private'}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('visibilityPrivateHint') || 'Only you'}
                  </div>
                </button>
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('location')}</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="mobile-input"
              placeholder={t('enterLocation')}
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('contact')}</label>
            <select
              name="contact_id"
              value={formData.contact_id}
              onChange={handleInputChange}
              className="mobile-input"
              disabled={dataLoading?.contacts}
            >
              <option value="">
                {dataLoading?.contacts ? t('loadingContacts') : t('selectContact')}
              </option>
              {contacts.map(contact => (
                <option key={contact.id} value={contact.id}>{contact.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">{t('notes')}</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={4}
            className="mobile-input resize-none"
            placeholder={t('addNotes')}
          />
        </div>

        {/* Reminder Checkbox - PRO ONLY, mobile only */}
        {!isDesktop && (
        <div className="flex items-center space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <input
            type="checkbox"
            id="reminder-checkbox-edit"
            name="reminder_enabled"
            checked={formData.reminder_enabled}
            onChange={(e) => {
              if (e.target.checked && !hasProAccess(user)) {
                alert('Task reminders are a Pro feature. Upgrade to Pro to enable reminders.');
                return;
              }
              handleInputChange(e);
            }}
            disabled={!hasProAccess(user) && !formData.reminder_enabled}
            className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
          />
          <label htmlFor="reminder-checkbox-edit" className="flex-1 cursor-pointer">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-gray-900">⏰ {t('enableReminder') || 'Enable Reminder'}</span>
              {!hasProAccess(user) && <ProBadge size="sm" />}
            </div>
            <p className="text-sm text-gray-600">
              {t('reminderDescription') || 'Receive a reminder 1 hour before the due time via your preferred method (Email/SMS)'}
            </p>
          </label>
        </div>
        )}

        {/* Existing Files */}
        {existingFiles.length > 0 && (
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-3">{t('currentAttachments')}</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {existingFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{getFileIcon(file)}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-48">
                        {file.filename}
                      </p>
                      <p className="text-xs text-gray-500">
                        {file.content_type}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExistingFile(file.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Upload Section */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-3">{t('addNewAttachments')}</label>
          
          <DriveAttachGate>
          <div className="grid grid-cols-3 gap-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="edit-file-upload"
                accept="*/*"
              />
              <label htmlFor="edit-file-upload" className="cursor-pointer">
                <div className="text-2xl mb-2">📁</div>
                <p className="text-sm text-gray-600">{t('uploadFiles')}</p>
              </label>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                className={`mx-auto h-8 w-8 rounded-full flex items-center justify-center ${
                  isRecording ? 'bg-red-500 text-white animate-pulse' : 'text-gray-400'
                }`}
              >
                {isRecording ? '⏹' : '🎤'}
              </button>
              <p className="text-sm text-gray-600 mt-2">
                {isRecording ? t('recording') : t('recordAudio')}
              </p>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                className="hidden"
                id="edit-photo"
              />
              <label htmlFor="edit-photo" className="cursor-pointer">
                <div className="text-2xl mb-2">📷</div>
                <p className="text-sm text-gray-600">{t('takePhoto')}</p>
              </label>
            </div>
          </div>
          </DriveAttachGate>

          {attachments.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                New Attachments ({attachments.length})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{getFileIcon(file)}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-48">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {uploadProgress.total > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span>Uploading: {uploadProgress.filename}</span>
                <span>{uploadProgress.current}/{uploadProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex space-x-3 pt-4 border-t mobile-form-buttons">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            disabled={loading}
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
            disabled={loading}
          >
            {loading ? t('updating') : t('updateTask')}
          </button>
        </div>
      </form>
      
      {/* Conflict Warning Modal */}
      {showConflictModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {conflictData.exactMatches.length > 0 ? `⚠️ ${t('timeConflictCannotSave')}` : `⚠️ ${t('schedulingConflictWarning')}`}
              </h2>
              <button onClick={handleEditTime} className="text-gray-400 hover:text-gray-600 p-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Exact Match Error */}
              {conflictData.exactMatches.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-red-800 mb-2">{t('exactTimeConflictDetected')}</h3>
                      <p className="text-sm text-red-700 mb-3">
                        {t('exactTimeConflictMessage')}
                      </p>
                      <div className="space-y-2">
                        {conflictData.exactMatches.map((task) => (
                          <div key={task.id} className="bg-white p-3 rounded border border-red-200">
                            <p className="font-medium text-gray-900">{task.title}</p>
                            <div className="flex items-center mt-1 text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>{new Date(task.due_date).toLocaleDateString()} {t('at')} {task.time}</span>
                              <span className="ml-3 px-2 py-0.5 bg-gray-100 rounded text-xs">{task.priority}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 2-Hour Window Warning */}
              {conflictData.exactMatches.length === 0 && conflictData.conflicts.length > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-yellow-800 mb-2">{t('nearbyTasksDetected')}</h3>
                      <p className="text-sm text-yellow-700 mb-3">
                        {t('nearbyTasksMessage').replace('{count}', conflictData.conflicts.length).replace('{plural}', conflictData.conflicts.length > 1 ? 's' : '')}
                      </p>
                      <div className="space-y-2">
                        {conflictData.conflicts.map((task) => (
                          <div key={task.id} className="bg-white p-3 rounded border border-yellow-200">
                            <p className="font-medium text-gray-900">{task.title}</p>
                            <div className="flex items-center mt-1 text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>{new Date(task.due_date).toLocaleDateString()} {t('at')} {task.time}</span>
                              <span className="ml-3 px-2 py-0.5 bg-gray-100 rounded text-xs">{task.priority}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t">
                {conflictData.exactMatches.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={handleEditTime}
                      className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleEditTime}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700"
                    >
                      {t('editTime')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleEditTime}
                      className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
                    >
                      {t('editTime')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveAnyway}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700"
                    >
                      {t('saveAnyway')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditTaskModal;